// Common Constants
export const TURIYA_ENGINE_PREFIX = 'turiya-engine';
export const API_WORKFLOW_KEY = 'Process-ckyc-dash';
export const API_WORKFLOW_BUSINESS_KEY_FOR_NON_EXTERNAL_USERS = 'dashboard';
export const BULK_TRIGGER_XLSX_WORKFLOW_KEY = 'Process-egzYWizULy';
export const PROCESSED_DAF_TASK_KEY = 'Activity_106wpx8';
export const MANUAL_TASK_WORKFLOW_KEY = 'Process-jpFRSOqsA';

// Protium Constants

const PROD_AUTH_BASE_URL = 'https://turiya.gsft.protium.co.in';
const PROD_ENGINE_BASE_URL = 'https://engine.gsft.protium.co.in';
const PROD_APP_ID = '3d2cd8b0-2378-46f2-878f-9526d4597358';

const UAT_AUTH_BASE_URL = 'https://turiya.gsft.protium.net.in';
const UAT_ENGINE_BASE_URL = 'https://engine.gsft.protium.net.in';
const UAT_APP_ID = 'a4008cfa-75d4-4314-bfe2-e14a8ff07562';

// Identity-provider aliases as configured in the Turiya Keycloak realm (`protium`).
// The UI only ever passes the alias to `loginWithIdentityProvider`; the provider
// itself is Keycloak configuration, not code.
const MICROSOFT_IDENTITY_PROVIDER = 'azure-corporate-directory';
const GOOGLE_IDENTITY_PROVIDER = 'google-corporate-directory';

export const UNIFIED_AUTH_CLIENT_ID = 'unified-turiya-app-ckyc-data-processing';

// External UAT Constants

const EXTERNAL_UAT_AUTH_BASE_URL = 'https://phoenix-uat.turiya.cloud';
const EXTERNAL_UAT_ENGINE_BASE_URL = 'https://engine.phoenix-uat.turiya.cloud';
const EXTERNAL_UAT_APP_ID = '1da9b47e-0f88-4e13-b269-6b6d3154cfda';

// Western Capital Constants

const WESTERN_CAPITAL_AUTH_BASE_URL = 'https://western-capital.turiya.cloud';
const WESTERN_CAPITAL_ENGINE_BASE_URL = 'https://engine.western-capital.turiya.cloud';
const WESTERN_CAPITAL_APP_ID = 'b4505205-ae0a-4e84-b499-752dcad159ae';

// Credit Saison Constants

const CREDIT_SAISON_AUTH_BASE_URL = 'https://credit-saison.turiya.cloud';
const CREDIT_SAISON_ENGINE_BASE_URL = 'https://engine.credit-saison.turiya.cloud';
const CREDIT_SAISON_APP_ID = '94775aa3-dd9b-4662-b11c-344237da3108';

// Phoenix lite Constants

const PHOENIX_LITE_AUTH_BASE_URL = 'https://lite.turiya.cloud';
const PHOENIX_LITE_ENGINE_BASE_URL = 'https://engine.lite.turiya.cloud';
const PHOENIX_LITE_APP_ID = 'b7c54878-70ee-413d-9c6a-609a1cbbd13e';

export interface IdentityProvider {
    /** Keycloak identity-provider alias, passed to `loginWithIdentityProvider`. */
    alias: string;
    /** Label shown on the sign-in button. */
    label: string;
}

/**
 * Sign-in options offered by Protium tenant environments, in display order.
 * The first entry is the default used for automatic login, so dropping Microsoft
 * later is a one-line deletion here (Google then becomes the default).
 */
const PROTIUM_IDENTITY_PROVIDERS: IdentityProvider[] = [
    { alias: MICROSOFT_IDENTITY_PROVIDER, label: 'Employee Login' },
    { alias: GOOGLE_IDENTITY_PROVIDER, label: 'Employee Login (Google)' },
];

export interface StaticEnvironment {
    authBaseUrl: string;
    engineBaseUrl: string;
    appId: string;
    enableSSO: boolean;
    enableSSOAutomaticLogin: boolean;
    identityProviders: IdentityProvider[];
}

export interface DynamicEnvironment {
    isCheckerEnabled: boolean;
    isCreateBatchCheckerEnabled: boolean;
    openReplayEnabled: boolean;
    openReplayProjectKey: string;
    openReplayIngestPoint: string;
    emailDomainValidation: boolean;
    userManagementEnabled: boolean;
}

export type Environment = StaticEnvironment & DynamicEnvironment;

export enum Environments {
    PROD = 'PROD',
    UAT = 'UAT',
    EXTERNAL_UAT = 'EXTERNAL_UAT',
    WESTERN_CAPITAL = 'WESTERN_CAPITAL',
    CREDIT_SAISON = 'CREDIT_SAISON',
    PHOENIX_LITE = 'PHOENIX_LITE',
}

export const STATIC_ENVIRONMENT: Record<Environments, StaticEnvironment> = {
    [Environments.PROD]: {
        authBaseUrl: PROD_AUTH_BASE_URL,
        engineBaseUrl: PROD_ENGINE_BASE_URL,
        appId: PROD_APP_ID,
        enableSSO: true,
        enableSSOAutomaticLogin: false,
        identityProviders: PROTIUM_IDENTITY_PROVIDERS,
    },
    [Environments.UAT]: {
        authBaseUrl: UAT_AUTH_BASE_URL,
        engineBaseUrl: UAT_ENGINE_BASE_URL,
        appId: UAT_APP_ID,
        enableSSO: true,
        // Automatic login is deliberately OFF on UAT: the Microsoft IdP (`azure-corporate-directory`)
        // is currently down (expired Entra client secret), and any user who has ever used SSO carries
        // `phoenix:sso-provider = azure-corporate-directory`, so auto-login would re-enter the dead
        // provider and never show the chooser. Showing both buttons is the working path.
        enableSSOAutomaticLogin: false,
        identityProviders: PROTIUM_IDENTITY_PROVIDERS,
    },
    [Environments.EXTERNAL_UAT]: {
        authBaseUrl: EXTERNAL_UAT_AUTH_BASE_URL,
        engineBaseUrl: EXTERNAL_UAT_ENGINE_BASE_URL,
        appId: EXTERNAL_UAT_APP_ID,
        enableSSO: false,
        enableSSOAutomaticLogin: false,
        identityProviders: [],
    },
    [Environments.WESTERN_CAPITAL]: {
        authBaseUrl: WESTERN_CAPITAL_AUTH_BASE_URL,
        engineBaseUrl: WESTERN_CAPITAL_ENGINE_BASE_URL,
        appId: WESTERN_CAPITAL_APP_ID,
        enableSSO: false,
        enableSSOAutomaticLogin: false,
        identityProviders: [],
    },
    [Environments.CREDIT_SAISON]: {
        authBaseUrl: CREDIT_SAISON_AUTH_BASE_URL,
        engineBaseUrl: CREDIT_SAISON_ENGINE_BASE_URL,
        appId: CREDIT_SAISON_APP_ID,
        enableSSO: false,
        enableSSOAutomaticLogin: false,
        identityProviders: [],
    },
    [Environments.PHOENIX_LITE]: {
        authBaseUrl: PHOENIX_LITE_AUTH_BASE_URL,
        engineBaseUrl: PHOENIX_LITE_ENGINE_BASE_URL,
        appId: PHOENIX_LITE_APP_ID,
        enableSSO: false,
        enableSSOAutomaticLogin: false,
        identityProviders: [],
    },
};

export const NOOP_DYNAMIC_ENVIRONMENT: DynamicEnvironment = {
    isCheckerEnabled: true,
    isCreateBatchCheckerEnabled: true,
    openReplayEnabled: false,
    openReplayProjectKey: '',
    openReplayIngestPoint: '',
    emailDomainValidation: true,
    userManagementEnabled: false,
};
